import {
  BOARD_COLS,
  BOARD_ROWS,
  cellCoord,
  START_CELL,
  type LaddersBoard,
} from "@bing/engine-ladders";

/* ------------------------------------------------------------------ *
 * مار پله — the board's GEOMETRY, as pure data (card-86, batch B1).
 *
 * Everything here is in BOARD UNITS — one cell is 1 × 1 — so the SVG's
 * viewBox, the pieces layer, the motion and the tests all read the same
 * numbers, and `sizePx` only scales them. x runs RIGHT, y runs DOWN.
 *
 *   ┌ FRAME ───────────────────────────┐   the gold frame, FRAME thick
 *   │ 100  99 …                    91  │   row 9 (the top)
 *   │  …                               │
 *   │   1   2 …                    10  │   row 0 (the bottom)
 *   └──────────────────────────────────┘   ← the SQUARE: `sizePx` wide
 *     [start pad: the pieces on 0]          ← PAD_H below the square
 *
 * ⚠️ NO MIRRORING, EVER. Every coordinate is physical and comes from the
 * engine's `cellCoord`, so a `dir="rtl"` ancestor cannot move a cell (a CSS
 * grid would inherit it and silently put 100 top-RIGHT, away from the
 * temple). Every snake's head and tail and every ladder's foot and top IS
 * its cell's centre — `test/ladders-geometry.test.ts` pins all six boards.
 * ------------------------------------------------------------------ */

/** The gold frame's thickness on every side of the 10 × 10 grid. */
export const FRAME = 0.26;
/** The square (grid + frame), in board units — `sizePx` is this many units. */
export const SQUARE = BOARD_COLS + 2 * FRAME;
/** The start-pad strip below the square, where every piece on 0 waits. */
export const PAD_H = 0.78;
/** The whole board element's height over its width (square + pad strip): the scene reserves it. */
export const BOARD_ASPECT = (SQUARE + PAD_H) / SQUARE;

/** A piece's radius in board units (a disc 0.54 cells across). */
export const PIECE_R = 0.27;

export interface Pt {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Cell `n`'s square, in board units. */
export function cellRect(n: number): Rect {
  const { row, col } = cellCoord(n);
  return { x: FRAME + col, y: FRAME + (BOARD_ROWS - 1 - row), w: 1, h: 1 };
}

/** Cell `n`'s centre — where every snake, ladder and lone piece on it is anchored. */
export function cellCentre(n: number): Pt {
  const r = cellRect(n);
  return { x: r.x + 0.5, y: r.y + 0.5 };
}

/* ---- the start pad (position 0) ----------------------------------------------------------- */

/** The pad plate under the bottom-left corner — four fixed slots, one per seat. */
export const PAD_SLOT_GAP = 0.64;
export const PAD_RECT: Rect = {
  x: FRAME,
  y: SQUARE + 0.06,
  w: 0.36 + 3 * PAD_SLOT_GAP + 0.36 + 0.06,
  h: PAD_H - 0.1,
};

/** Where seat `seat`'s piece waits on 0: its own slot, so the pad never reshuffles. */
export function padSlot(seat: number): Pt {
  return { x: PAD_RECT.x + 0.39 + seat * PAD_SLOT_GAP, y: PAD_RECT.y + PAD_RECT.h / 2 };
}

/* ---- pieces sharing a cell ----------------------------------------------------------------- */

/** Offsets for 1..4 pieces on one cell, by their rank there (seat order) — never random. */
const SHARE: readonly (readonly Pt[])[] = [
  [{ x: 0, y: 0 }],
  [
    { x: -0.19, y: -0.06 },
    { x: 0.19, y: 0.06 },
  ],
  [
    { x: -0.19, y: -0.14 },
    { x: 0.19, y: -0.14 },
    { x: 0, y: 0.17 },
  ],
  [
    { x: -0.19, y: -0.17 },
    { x: 0.19, y: -0.17 },
    { x: -0.19, y: 0.17 },
    { x: 0.19, y: 0.17 },
  ],
];

/**
 * Every seat's piece centre for `positions` (0 = the pad). Pieces sharing a cell spread by
 * their rank in seat order — a pure function of the positions, so every phone draws the same.
 */
export function pieceLayout(positions: readonly number[]): Pt[] {
  return positions.map((cell, seat) => {
    if (cell === START_CELL) return padSlot(seat);
    const here = positions.flatMap((c, s) => (c === cell ? [s] : []));
    const rank = here.indexOf(seat);
    const set = SHARE[Math.min(here.length, SHARE.length) - 1] ?? SHARE[0]!;
    const off = set[Math.min(rank, set.length - 1)] ?? { x: 0, y: 0 };
    const c = cellCentre(cell);
    return { x: c.x + off.x, y: c.y + off.y };
  });
}

/* ---- snakes -------------------------------------------------------------------------------- */

export interface SnakeGeom {
  readonly index: number;
  /** === `cellCentre(snake.head)`. */
  readonly head: Pt;
  /** === `cellCentre(snake.tail)` — the tail TIP. */
  readonly tail: Pt;
  /** The spine sampled head → tail: `spine[0]` is `head`, the last sample is `tail`. */
  readonly spine: readonly Pt[];
  /** The body's half-width at each spine sample — a neck, a full body, a tip of 0. */
  readonly halfWidth: readonly number[];
  /** The unit normal at each sample (left of the head → tail direction). */
  readonly normal: readonly Pt[];
  /** Which way the head looks (a unit vector, away from the body). */
  readonly facing: Pt;
}

/** The widest body half-width, in board units. */
export const SNAKE_HALF_W = 0.17;

/**
 * A snake's body: head → tail along a smooth wiggle. The wiggle is a PURE function of its index
 * and its length — a whole number of half-waves (so it is 0 at both ends and the endpoints sit
 * exactly on the cell centres), its side and its amplitude set by the index — so two snakes on a
 * board never wiggle alike, and every phone draws the same snake.
 */
export function snakeGeom(board: LaddersBoard, index: number): SnakeGeom {
  const s = board.snakes[index];
  if (!s) throw new RangeError(`snakeGeom: board ${board.id} has no snake ${index}`);
  const head = cellCentre(s.head);
  const tail = cellCentre(s.tail);
  const dx = tail.x - head.x;
  const dy = tail.y - head.y;
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  const halfWaves = Math.max(2, Math.round(len / 1.45)) + (index % 2);
  const side = index % 2 === 0 ? 1 : -1;
  const amp = side * Math.min(0.27 + 0.07 * ((index * 5) % 3), len * 0.2);
  const n = Math.min(72, Math.max(24, Math.round(14 + len * 7)));

  const spine: Pt[] = [];
  const halfWidth: number[] = [];
  // A wiggle near the board's edge is flattened against it, never drawn over the frame.
  const inside = (v: number, hw: number): number =>
    Math.max(FRAME + hw + 0.04, Math.min(SQUARE - FRAME - hw - 0.04, v));
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    // A neck just behind the head, the full body, then a long taper to a point.
    const w = t < 0.1 ? 0.72 + 0.28 * (t / 0.1) : Math.pow(Math.max(0, 1 - (t - 0.1) / 0.9), 0.62);
    const hw = SNAKE_HALF_W * w;
    halfWidth.push(hw);
    const off = amp * Math.sin(Math.PI * halfWaves * t);
    spine.push(
      i === 0
        ? head
        : i === n
          ? tail
          : {
              x: inside(head.x + dx * t - uy * off, hw),
              y: inside(head.y + dy * t + ux * off, hw),
            },
    );
  }
  const normal = spine.map((_, i) => {
    const a = spine[Math.max(0, i - 1)]!;
    const b = spine[Math.min(spine.length - 1, i + 1)]!;
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return { x: -(b.y - a.y) / l, y: (b.x - a.x) / l };
  });
  const first = spine[1]!;
  const fl = Math.hypot(head.x - first.x, head.y - first.y) || 1;
  const facing = { x: (head.x - first.x) / fl, y: (head.y - first.y) / fl };
  return { index, head, tail, spine, halfWidth, normal, facing };
}

/** The point at fraction `t` (0 = head, 1 = tail) along a snake's spine samples. */
export function spineAt(g: SnakeGeom, t: number): { readonly p: Pt; readonly i: number } {
  const last = g.spine.length - 1;
  const i = Math.max(0, Math.min(last, Math.round(t * last)));
  return { p: g.spine[i]!, i };
}

/* ---- ladders ------------------------------------------------------------------------------- */

export interface LadderGeom {
  readonly index: number;
  /** === `cellCentre(ladder.foot)`. */
  readonly foot: Pt;
  /** === `cellCentre(ladder.top)`. */
  readonly top: Pt;
  /** The two rails, each [at the foot, at the top] — parallel to the foot → top line. */
  readonly rails: readonly [readonly [Pt, Pt], readonly [Pt, Pt]];
  /** The rungs, foot → top, each [left rail, right rail]. */
  readonly rungs: readonly (readonly [Pt, Pt])[];
}

/** Half the ladder's width (rail to centre line). */
export const LADDER_HALF_W = 0.2;
/** Roughly one rung per this many cells of length. */
export const RUNG_GAP = 0.42;

/**
 * A ladder: foot → top, two rails and a rung count set by its LENGTH. Its rails sit either side of
 * the foot → top line — no ladder is ever drawn over another: the engine's `boardProblems` refuses
 * a board where two lie on one line or an end sits on another's body («collinear», «touch»).
 */
export function ladderGeom(board: LaddersBoard, index: number): LadderGeom {
  const l = board.ladders[index];
  if (!l) throw new RangeError(`ladderGeom: board ${board.id} has no ladder ${index}`);
  const foot = cellCentre(l.foot);
  const top = cellCentre(l.top);
  const dx = top.x - foot.x;
  const dy = top.y - foot.y;
  const len = Math.hypot(dx, dy);
  const ux = -dy / len;
  const uy = dx / len;
  const side = (d: number): [Pt, Pt] => [
    { x: foot.x + ux * d, y: foot.y + uy * d },
    { x: top.x + ux * d, y: top.y + uy * d },
  ];
  const left = side(LADDER_HALF_W);
  const right = side(-LADDER_HALF_W);
  const count = Math.max(2, Math.round(len / RUNG_GAP));
  const rungs: [Pt, Pt][] = [];
  for (let k = 0; k < count; k += 1) {
    const t = (k + 0.5) / count;
    rungs.push([
      { x: left[0].x + dx * t, y: left[0].y + dy * t },
      { x: right[0].x + dx * t, y: right[0].y + dy * t },
    ]);
  }
  return { index, foot, top, rails: [left, right], rungs };
}

/* ---- path strings (3 decimals: crisp at any size, small markup) ---------------------------- */

const f = (v: number): string => (Math.round(v * 1000) / 1000).toString();

/** `M x y L …` through `pts`; closed when asked. */
export function polyPath(pts: readonly Pt[], close = false): string {
  return (
    pts.map((p, i) => `${i === 0 ? "M" : "L"}${f(p.x)} ${f(p.y)}`).join("") + (close ? "Z" : "")
  );
}

/** A snake body's outline (left side head → tail, right side back), as one closed path. */
export function snakeOutline(g: SnakeGeom, scale = 1): string {
  const left = g.spine.map((p, i) => ({
    x: p.x + g.normal[i]!.x * g.halfWidth[i]! * scale,
    y: p.y + g.normal[i]!.y * g.halfWidth[i]! * scale,
  }));
  const right = g.spine.map((p, i) => ({
    x: p.x - g.normal[i]!.x * g.halfWidth[i]! * scale,
    y: p.y - g.normal[i]!.y * g.halfWidth[i]! * scale,
  }));
  return polyPath([...left, ...right.reverse()], true);
}
